import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import type { Theme } from '@mui/material/styles';
import { Box } from '@mui/system';
import { palette } from '@openx/components-core/lib/theme/paletteV2';
import { DealRowActionType } from '@openx/components-deal/constants';
import { canArchiveDeal } from '@openx/components-deal/lib/utils/canArchiveDeal';
import { useDealContext } from '@openx/components-deal/lib/utils/context/DealContext';
import type { DealActionResult } from '@openx/components-deal/lib/utils/context/DealHookTypes';
import { useDealsOperations } from '@openx/components-deal/lib/utils/hooks/useDealsOperations';
import { isDealDeleted } from '@openx/components-deal/lib/utils/isDealDeleted';
import type { DealDetailsSectionProps } from '@openx/components-deal/types';
import type { ActionButtonProps } from '@openx/components-header/HeaderActions/ActionButton';
import { OPENX_AGENT_TOOL } from '@openx/components-openx-agent/components/Home/utils/consts';
import { OpenXAgentIcon } from '@openx/components-openx-agent/components/icons/OpenXAgentIcon';
import { useOpenXAgentContext } from '@openx/components-openx-agent/context/useOpenXAgentContext';
import { ACL_TYPE } from '@openx/types/aclType';
import { type DealOaApi, type OaDealStatus, oaDealStatus } from '@openx/types/deal';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useIsAllowed } from '../../../configs/permission/utils/useIsAllowed';
import { useDealChangeStatusRestriction } from '../../../utils/useDealChangeStatusRestriction';

type DealDetailsHeaderActionProps = {
  deal?: DealDetailsSectionProps<DealOaApi>['deal'];
  onDeleted?: (result?: DealActionResult) => void;
};

export const useHeaderActions = (props: DealDetailsHeaderActionProps) => {
  const { t } = useTranslation();
  const { deal, onDeleted } = props;
  const [dealUpdating, setDealUpdating] = useState<boolean>(false);
  const { setIsOpenXAgentOpen, setActiveTool } = useOpenXAgentContext();
  const { isAllowed } = useIsAllowed();

  const { useDealGroupFetch } = useDealContext();

  const { enqueueSnackbar } = useSnackbar();
  const restriction = useDealChangeStatusRestriction(deal ?? null);

  const { callDealAction } = useDealsOperations();
  const { group } = useDealGroupFetch(deal?.deal_id ?? '');
  const dealGroupWithRboDynamic = group.find(({ rboConfig }) => !!(rboConfig?.tactic === 'dynamic'));

  const navigate = useNavigate();

  const isDealActive = (status: DealOaApi['status']) => {
    return status.toLowerCase() === oaDealStatus.ACTIVE.toLowerCase();
  };

  const handleChangeStatus = (deal: DealOaApi) => {
    if (restriction.loading) return;

    if (restriction.blocked) {
      enqueueSnackbar(restriction.reason, { variant: 'error' });
      return;
    }

    const showRboAlert = !!dealGroupWithRboDynamic && group.length > 1;

    setDealUpdating(true);

    callDealAction({
      action: isDealActive(deal.status) ? DealRowActionType.PAUSE : DealRowActionType.RESUME,
      deals: [{ ...deal, id: deal.uid }],
      onFinish: () => setDealUpdating(false),
      skipModal: !isDealActive(deal.status) && !showRboAlert,
      showRBOAlert: showRboAlert,
    });
  };

  const handleUseDealAsTemplate = (deal: DealOaApi) => {
    navigate('/deals/create', {
      state: {
        dealId: deal?.id,
        useAsTemplate: true,
      },
    });
  };

  const handleUseDealSharedTargeting = (deal: DealOaApi) => {
    navigate('/deals/create', {
      state: {
        dealId: deal?.id,
        useSharedTargeting: true,
      },
    });
  };

  const targetingCheckAction: ActionButtonProps = {
    id: OPENX_AGENT_TOOL.TARGETING_EXPLAINER,
    color: 'secondary',
    startIcon: (
      <Box sx={{ mb: 0.25 }}>
        <OpenXAgentIcon width={16} height={16} />
      </Box>
    ),
    'data-test': 'check-targeting',
    label: t('Check Targeting'),
    onClick: () => {
      setActiveTool({
        toolId: OPENX_AGENT_TOOL.TARGETING_EXPLAINER,
        toolData: deal ?? null,
      });
      setIsOpenXAgentOpen(true);
    },
    isAllowed: isAllowed(isAllowedByCompiledAcl(ACL_TYPE.XPERT_ENABLED)),
    isHidden: !isAllowed(isAllowedByCompiledAcl(ACL_TYPE.XPERT_ENABLED)),
    sx: {
      background: (theme: Theme) =>
        `linear-gradient(310.6deg, ${theme.palette.primary.main} 6.51%, ${theme.palette.background.yellowLight} 87.85%)`,
    },
    showAsActionButton: true,
  };

  if (!deal) {
    return { actions: [targetingCheckAction] };
  }

  const isDirectDeal = !!deal.is_direct_deal;
  const directDealNotAllowedMessage = t('This action is not available for direct deals.');

  const pauseDealAction: ActionButtonProps = {
    color: 'secondary',
    'data-test': 'deal-pause',
    isAllowed: !isDirectDeal && deal.status.toLowerCase() !== oaDealStatus.PAUSED.toLowerCase() && !isDealDeleted(deal),
    label: t('Pause Deal'),
    loading: dealUpdating,
    notAllowedMessage: isDirectDeal ? directDealNotAllowedMessage : t('Only active deals can be paused.'),
    onClick: () => handleChangeStatus(deal),
  };

  const reactivateDealAction: ActionButtonProps = {
    color: 'secondary',
    'data-test': 'deal-reactivate',
    isAllowed: !isDirectDeal && deal.status.toLowerCase() === oaDealStatus.PAUSED.toLowerCase() && !isDealDeleted(deal),
    label: t('Reactivate Deal'),
    loading: dealUpdating,
    notAllowedMessage: isDirectDeal ? directDealNotAllowedMessage : t('Only Deals in Paused state can be activated'),
    onClick: () => handleChangeStatus(deal),
  };

  const changeDealStatusAction =
    deal.status.toLowerCase() === oaDealStatus.ACTIVE.toLowerCase() ? pauseDealAction : reactivateDealAction;

  const actions: ActionButtonProps[] = [
    targetingCheckAction,
    {
      color: 'secondary',
      'data-test': 'edit',
      isAllowed: !isDirectDeal && !isDealDeleted(deal),
      label: t('Edit'),
      notAllowedMessage: isDirectDeal ? directDealNotAllowedMessage : t('Cannot edit deal because the deal is deleted'),
      onClick: () => navigate(`/deals/${deal.uid}/edit`),
      showAsActionButton: true,
    },
    {
      color: 'error',
      'data-test': 'deal-delete',
      isAllowed: !isDirectDeal && !dealUpdating && canArchiveDeal(deal.status as OaDealStatus) && !isDealDeleted(deal),
      isHidden: isDealDeleted(deal),
      label: t('Delete'),
      notAllowedMessage: isDirectDeal
        ? directDealNotAllowedMessage
        : isDealActive(deal.status)
          ? t('Deal must be paused to Delete')
          : t('Cannot delete deal because the deal is in publishing state'),
      onClick: () =>
        callDealAction({ action: DealRowActionType.DELETE, deals: [{ ...deal, id: deal.uid }], onFinish: onDeleted }),
      showAsActionButton: true,
    },
    {
      color: 'secondary',
      'data-test': 'deal-insights',
      endIcon: <OpenInNewIcon sx={{ fontSize: '18px !important' }} />,
      isAllowed: !isDirectDeal,
      isHidden: false,
      label: t('Deal insights'),
      notAllowedMessage: directDealNotAllowedMessage,
      onClick: () => {
        navigate(`/deal-insights?ox_deal_id=${deal.id}&deal_name=${encodeURIComponent(deal.name)}`);
      },
      showAsActionButton: true,
      sx: {
        backgroundColor: `${palette.background.paper} !important`,
        color: `${palette.text.secondary} !important`,
      },
    },
    changeDealStatusAction,
    {
      color: 'secondary',
      'data-test': 'deal-download',
      isAllowed: !isDirectDeal,
      label: t('Download'),
      notAllowedMessage: directDealNotAllowedMessage,
      onClick: () => callDealAction({ action: DealRowActionType.DOWNLOAD, deals: [{ ...deal, id: deal.uid }] }),
    },
    {
      color: 'secondary',
      'data-test': 'use-as-template',
      isAllowed: !isDirectDeal,
      label: t('Use As Template'),
      notAllowedMessage: directDealNotAllowedMessage,
      onClick: () => handleUseDealAsTemplate(deal),
    },
    {
      color: 'secondary',
      'data-test': 'use-shared-targeting',
      isAllowed: !isDirectDeal,
      label: t('Use Shared Targeting'),
      notAllowedMessage: directDealNotAllowedMessage,
      onClick: () => handleUseDealSharedTargeting(deal),
    },
  ];

  return { actions };
};
